// Mail d'envoi du lien d'installation (Apple Mail / Outlook) : texte proposé le 23/09/2026, à
// valider par Aurélien. Pur : le contenu est calculé ici et testé ; l'envoi est dans la fonction
// send-install-links (Gmail API, délégation gmail.send), jamais par un cron ni par auto_push.
import type { Person } from './types.ts';
import { esc } from './util.ts';

export interface InstallMail {
  to: string;
  subject: string;
  text: string;
  html: string;
}

/** Lien personnel de la page d'installation (jeton PRIVÉ install_token, jamais public_token). */
export function installLink(publicBaseUrl: string, person: Pick<Person, 'install_token'>): string {
  return `${publicBaseUrl.replace(/\/$/, '')}/install/${person.install_token}`;
}

export const INSTALL_MAIL_SUBJECT = 'Votre signature mail — installation sur Apple Mail / Outlook';

/** Cibles de l'envoi : boîtes gérées, actives, marquées « Apple Mail / Outlook » ; `only` restreint à ces adresses. */
export function installTargets<P extends Pick<Person, 'primary_email' | 'active' | 'managed' | 'local_client' | 'kind'>>(people: P[], only: string[] | null = null): P[] {
  const wanted = only ? new Set(only.map((e) => e.toLowerCase().trim())) : null;
  return people.filter((p) => p.active && p.managed && (wanted ? wanted.has(p.primary_email.toLowerCase()) : p.local_client === true));
}

/** Suffixe du nom d'expéditeur affiché : « Victor Rocher — Bonjour/Bonsoir ». */
export const SENDER_SUFFIX = 'Bonjour/Bonsoir';

export interface InstallSender {
  email: string;
  /** Nom affiché dans l'en-tête From et en signature du mail, déduit de l'annuaire. */
  name: string;
}

/**
 * L'expéditeur des liens d'installation est la personne CONNECTÉE qui déclenche l'envoi (décision du
 * 06/10/2026), jamais une adresse fixe : les réponses lui arrivent. Son nom affiché vient de l'annuaire.
 * Si l'adresse n'y est pas, ou si sa boîte est suspendue ou inactive, on REFUSE en nommant l'adresse et
 * la raison : aucun repli silencieux sur une autre identité.
 */
export function installSender<P extends Pick<Person, 'primary_email' | 'first_name' | 'last_name' | 'display_name' | 'active'> & { google_suspended?: boolean | null }>(
  people: P[],
  email: string,
): { ok: true; sender: InstallSender } | { ok: false; reason: string } {
  const addr = email.toLowerCase().trim();
  const p = people.find((x) => x.primary_email.toLowerCase() === addr);
  const refuse = (why: string) => ({ ok: false as const, reason: `Impossible d’envoyer depuis ${addr} : ${why}. Aucun message envoyé, aucun repli sur une autre adresse.` });
  if (!p) return refuse('adresse absente de l’annuaire (synchronisez l’annuaire, ou l’adresse n’est pas une boîte Google Workspace du groupe)');
  if (p.google_suspended) return refuse('boîte suspendue dans Google Workspace');
  if (!p.active) return refuse('boîte marquée inactive dans Personnes');
  const person = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.display_name || '';
  if (!person) return refuse('ni prénom ni nom dans l’annuaire pour composer le nom d’expéditeur');
  return { ok: true, sender: { email: p.primary_email, name: `${person} — ${SENDER_SUFFIX}` } };
}

/** Le mail, personnalisé : prénom (ou « Bonjour, » pour une boîte partagée), lien, signataire = l'expéditeur réel. */
export function installMail(person: Pick<Person, 'primary_email' | 'first_name' | 'display_name' | 'kind' | 'install_token'>, publicBaseUrl: string, sender: { name: string }): InstallMail {
  const link = installLink(publicBaseUrl, person);
  const hello = person.kind === 'person' && person.first_name ? `Bonjour ${person.first_name},` : 'Bonjour,';
  const lines = [
    hello,
    '',
    'Nous passons de Signitic à notre propre outil de signatures. Sur Gmail (site et application), votre nouvelle signature est déjà en place : vous n’avez rien à faire.',
    '',
    'Si vous écrivez aussi depuis Apple Mail ou Outlook, installez-la une fois en suivant votre lien personnel :',
    link,
    '',
    'La page vous guide pas à pas : copier la signature, la coller, puis la choisir comme signature du compte — sans cette dernière étape, elle n’apparaît pas dans vos messages. Ensuite, les bannières événements se mettent à jour toutes seules : inutile de réinstaller.',
    '',
    'Ce lien est personnel : ne le transférez pas.',
    '',
    'Une question ? Répondez simplement à ce mail.',
    '',
    sender.name,
  ];
  const text = lines.join('\n');
  const para = (s: string) => `<p style="margin:0 0 12px">${esc(s)}</p>`;
  const html = `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#1a1a1a">` +
    para(hello) +
    para(lines[2]) +
    para(lines[4]) +
    `<p style="margin:0 0 12px"><a href="${esc(link)}" style="color:#1a1a1a">${esc(link)}</a></p>` +
    para(lines[7]) +
    para(lines[9]) +
    para(lines[11]) +
    para(sender.name) +
    `</div>`;
  return { to: person.primary_email, subject: INSTALL_MAIL_SUBJECT, text, html };
}
